// The period filter every history surface in the administration offers, in one
// place: the wireframe's own `FiltruPerioada`
// (wireframes/react/src/components/FiltruPerioada.tsx), whose header comment
// says why it is shared — "ca «ce s-a schimbat săptămâna trecută / între X și
// Y» să se poată afla oriunde există un istoric".
//
// One pattern in the whole system is the owner's ruling of 2026-08-23; this
// module is what keeps the alert trigger history and the aggregate fișa's
// Istoric from drifting into two vocabularies of the same six presets.

/** The wireframe's presets, in its order. The seventh entry is "all time" and
 *  is the empty value, so an unfiltered address carries no parameter at all. */
export const HISTORY_PERIODS = ['7d', '30d', 'month', 'last_month', 'custom'] as const;

export type HistoryPeriodId = (typeof HISTORY_PERIODS)[number] | '';

function isoDay(offsetDays: number): string {
  const date = new Date();
  date.setDate(date.getDate() - offsetDays);
  return date.toISOString().slice(0, 10);
}

function localDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * Resolves a preset to the inclusive from/to pair a history endpoint filters
 * on. `custom` passes the two dates through untouched, so a half-filled custom
 * interval stays half-bounded rather than snapping to a default the reader did
 * not choose.
 */
export function periodRange(
  period: string,
  from: string,
  to: string,
): { from: string; to: string } {
  const today = new Date();
  const monthStart = `${today.toISOString().slice(0, 8)}01`;
  if (period === '7d') return { from: isoDay(6), to: isoDay(0) };
  if (period === '30d') return { from: isoDay(29), to: isoDay(0) };
  if (period === 'month') return { from: monthStart, to: isoDay(0) };
  if (period === 'last_month') {
    const first = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const last = new Date(today.getFullYear(), today.getMonth(), 0);
    return { from: localDay(first), to: localDay(last) };
  }
  if (period === 'custom') return { from, to };
  return { from: '', to: '' };
}

/**
 * Whether a chosen period is worth showing as an active filter chip. A `custom`
 * with neither date entered narrows nothing, so it is not yet a criterion — the
 * wireframe's own rule (`chipPerioada` returns nothing for it).
 */
export function isPeriodActive(period: string, from: string, to: string): boolean {
  if (!period) return false;
  if (period !== 'custom') return true;
  return Boolean(from || to);
}
